// src/pages/orderAssignment/AssignmentRulesConfigPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Settings,
  Save,
  X,
  Plus,
  Trash2,
  DollarSign,
  Package,
  Users as UsersIcon,
  ArrowLeft,
  Info,
  Clock,
  User,
  AlertCircle,
  ToggleLeft,
  ToggleRight
} from 'lucide-react';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { getAssignmentRules, saveAssignmentRules } from '../../services/api/orderAssignments';
import { getShops } from '../../services/api/shops';
import { getUsers } from '../../services/api/users';
import { getProducts } from '../../services/api/products';
import { formatDateTime } from '../../utils/dateUtils';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import MultiSelectFilter from '../../components/common/MultiSelectFilter/MultiSelectFilter';
import styles from './AssignmentRulesConfigPage.module.css';

const AssignmentRulesConfigPage = () => {
  const navigate = useNavigate();
  const { t } = useLocalization();
  const { success, error: showError } = useAlert();

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [shops, setShops] = useState([]);
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedShopId, setSelectedShopId] = useState(null);
  const [rulesData, setRulesData] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  const [formData, setFormData] = useState({
    isEnabled: false,
    grandTotalRules: [],
    productRules: [],
    defaultUserIds: []
  });

  const [errors, setErrors] = useState({});

  // Load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setIsLoading(true);
        const [shopsResponse, usersResponse, productsResponse] = await Promise.all([
          getShops({assigned:true, type:'online,dropshipper'}),
          getUsers({ limit: 1000 }),
          getProducts({ limit: 1000 })
        ]);

        setShops(shopsResponse || []);
        setUsers(usersResponse?.users || []);
        setProducts(productsResponse?.products || []);

        // Set default shop
        const savedShopId = sessionStorage.getItem('assignmentRulesShopId');
        const defaultShop = shopsResponse?.find(shop =>
          savedShopId ? shop.id === parseInt(savedShopId) : shop.isDefault
        ) || shopsResponse?.[0];

        if (defaultShop) {
          setSelectedShopId(defaultShop.id);
        }
      } catch (err) {
        console.error('Failed to load initial data:', err);
        showError(t('orderAssignment.loadDataFailed'));
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialData();
  }, [showError, t]);

  // Load rules when shop changes
  useEffect(() => {
    const loadRules = async () => {
      if (!selectedShopId) return;

      try {
        const response = await getAssignmentRules(selectedShopId);
        setRulesData(response.rules);

        if (response.rules) {
          setFormData({
            isEnabled: response.rules.rules.isEnabled || false,
            grandTotalRules: response.rules.rules.grandTotalRules || [],
            productRules: response.rules.rules.productRules || [],
            defaultUserIds: response.rules.rules.defaultUserIds || []
          });
        } else {
          // No rules exist, reset to defaults
          setFormData({
            isEnabled: false,
            grandTotalRules: [],
            productRules: [],
            defaultUserIds: []
          });
        }
        setHasChanges(false);
      } catch (err) {
        console.error('Failed to load rules:', err);
        // Rules might not exist yet, which is okay
        setFormData({
          isEnabled: false,
          grandTotalRules: [],
          productRules: [],
          defaultUserIds: []
        });
        setHasChanges(false);
      }
    };

    loadRules();
  }, [selectedShopId]);

  const handleShopChange = (shopId) => {
    if (hasChanges) {
      if (!window.confirm(t('orderAssignment.unsavedChanges'))) {
        return;
      }
    }
    setSelectedShopId(shopId);
    sessionStorage.setItem('assignmentRulesShopId', shopId.toString());
    setHasChanges(false);
  };

  const handleToggleEnabled = () => {
    setFormData(prev => ({ ...prev, isEnabled: !prev.isEnabled }));
    setHasChanges(true);
  };

  // Grand Total Rules
  const addGrandTotalRule = () => {
    setFormData(prev => ({
      ...prev,
      grandTotalRules: [
        ...prev.grandTotalRules,
        { min: 0, max: null, userIds: [] }
      ]
    }));
    setHasChanges(true);
  };

  const updateGrandTotalRule = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      grandTotalRules: prev.grandTotalRules.map((rule, i) =>
        i === index ? { ...rule, [field]: value } : rule
      )
    }));
    setHasChanges(true);
  };

  const removeGrandTotalRule = (index) => {
    setFormData(prev => ({
      ...prev,
      grandTotalRules: prev.grandTotalRules.filter((_, i) => i !== index)
    }));
    setHasChanges(true);
  };

  // Product Rules
  const addProductRule = () => {
    setFormData(prev => ({
      ...prev,
      productRules: [
        ...prev.productRules,
        { productIds: [], userIds: [] }
      ]
    }));
    setHasChanges(true);
  };

  const updateProductRule = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      productRules: prev.productRules.map((rule, i) =>
        i === index ? { ...rule, [field]: value } : rule
      )
    }));
    setHasChanges(true);
  };

  const removeProductRule = (index) => {
    setFormData(prev => ({
      ...prev,
      productRules: prev.productRules.filter((_, i) => i !== index)
    }));
    setHasChanges(true);
  };

  // Default Users
  const updateDefaultUsers = (userIds) => {
    setFormData(prev => ({ ...prev, defaultUserIds: userIds }));
    setHasChanges(true);
  };

  // Validation
  const validateRules = () => {
    const newErrors = {};

    // Validate grand total rules
    formData.grandTotalRules.forEach((rule, index) => {
      if (rule.userIds.length === 0) {
        newErrors[`grandTotal_${index}_users`] = t('orderAssignment.atLeastOneUser');
      }

      if (rule.min !== null && rule.max !== null && rule.min >= rule.max) {
        newErrors[`grandTotal_${index}_range`] = t('orderAssignment.maxMustBeGreaterThanMin');
      }
    });

    // Validate product rules
    formData.productRules.forEach((rule, index) => {
      if (rule.productIds.length === 0) {
        newErrors[`product_${index}_products`] = t('orderAssignment.atLeastOneProduct');
      }
      if (rule.userIds.length === 0) {
        newErrors[`product_${index}_users`] = t('orderAssignment.atLeastOneUser');
      }
    });

    // Validate that at least one rule or default users exist if enabled
    if (formData.isEnabled) {
      const hasRules = formData.grandTotalRules.length > 0 || formData.productRules.length > 0;
      const hasDefaultUsers = formData.defaultUserIds.length > 0;

      if (!hasRules && !hasDefaultUsers) {
        newErrors.general = t('orderAssignment.atLeastOneRuleOrDefault');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateRules()) {
      showError(t('orderAssignment.fixValidationErrors'));
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        shopId: selectedShopId,
        rules: {
          isEnabled: formData.isEnabled,
          grandTotalRules: formData.grandTotalRules,
          productRules: formData.productRules,
          defaultUserIds: formData.defaultUserIds
        }
      };

      const response = await saveAssignmentRules(payload);
      setRulesData(response.rules);
      setHasChanges(false);
      success(t('orderAssignment.rulesSavedSuccess'));
    } catch (err) {
      console.error('Failed to save rules:', err);
      showError(err.response?.data?.message || t('orderAssignment.rulesSaveFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      if (!window.confirm(t('orderAssignment.unsavedChanges'))) {
        return;
      }
    }
    navigate('/orders');
  };

  // Format user options
  const userOptions = users.map(user => ({
    key: user.id,
    label: `${user.name} (${user.role?.name || 'No Role'})`
  }));

  // Format product options
  const productOptions = products.map(product => ({
    key: product.id,
    label: `${product.name} ${product.sku ? `(${product.sku})` : ''}`
  }));

  if (isLoading) {
    return (
      <div className={styles.configPage}>
        <PageHeader
          title={t('orderAssignment.configurationTitle')}
          description={t('orderAssignment.configurationSubtitle')}
          icon={Settings}
        />

        <div className={styles.pageContent}>
          <div className={styles.loadingState}>
            <div className={styles.loadingSpinnerLarge}></div>
            <h3>{t('orderAssignment.loadingConfig')}</h3>
            <p>{t('orderAssignment.loadingConfigMessage')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.configPage}>
      <PageHeader
        title={t('orderAssignment.configurationTitle')}
        description={t('orderAssignment.configurationSubtitle')}
        icon={Settings}
        actions={
          <button onClick={handleCancel} className={styles.backButton}>
            <ArrowLeft size={20} />
            {t('orderAssignment.backToOrders')}
          </button>
        }
      />

      <div className={styles.pageContent}>
        <div className={styles.configContainer}>
          {/* Shop Selection */}
          <div className={styles.configSection}>
            <div className={styles.sectionHeader}>
              <h2>{t('orderAssignment.shopSelection')}</h2>
            </div>
            <div className={styles.sectionContent}>
              <div className={styles.formGroup}>
                <label htmlFor="shopSelect">{t('orderAssignment.selectShop')} *</label>
                <select
                  id="shopSelect"
                  value={selectedShopId || ''}
                  onChange={(e) => handleShopChange(parseInt(e.target.value))}
                  className={styles.select}
                >
                  <option value="">{t('orderAssignment.selectShopPlaceholder')}</option>
                  {shops.map(shop => (
                    <option key={shop.id} value={shop.id}>
                      {shop.name} ({shop.type})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {selectedShopId && (
            <>
              {/* Master Toggle */}
              <div className={styles.configSection}>
                <div className={styles.sectionHeader}>
                  <h2>{t('orderAssignment.autoAssignmentStatus')}</h2>
                </div>
                <div className={styles.sectionContent}>
                  <div className={styles.toggleContainer}>
                    <button
                      onClick={handleToggleEnabled}
                      className={`${styles.toggleButton} ${formData.isEnabled ? styles.enabled : ''}`}
                    >
                      {formData.isEnabled ? (
                        <ToggleRight size={24} />
                      ) : (
                        <ToggleLeft size={24} />
                      )}
                      <span className={styles.toggleLabel}>
                        {formData.isEnabled ? t('orderAssignment.autoAssignmentEnabled') : t('orderAssignment.autoAssignmentDisabled')}
                      </span>
                    </button>
                    <p className={styles.toggleDescription}>
                      {formData.isEnabled
                        ? t('orderAssignment.enabledDescription')
                        : t('orderAssignment.disabledDescription')}
                    </p>
                  </div>
                </div>
              </div>

              {/* General Error */}
              {errors.general && (
                <div className={styles.errorBanner}>
                  <AlertCircle size={20} />
                  <p>{errors.general}</p>
                </div>
              )}

              {/* Grand Total Rules */}
              <div className={styles.configSection}>
                <div className={styles.sectionHeader}>
                  <div className={styles.sectionTitleRow}>
                    <DollarSign className={styles.sectionIcon} size={20} />
                    <h2>{t('orderAssignment.grandTotalRules')}</h2>
                  </div>
                  <button onClick={addGrandTotalRule} className={styles.addButton}>
                    <Plus size={16} />
                    {t('orderAssignment.addRule')}
                  </button>
                </div>
                <div className={styles.sectionContent}>
                  <div className={styles.infoBox}>
                    <Info size={16} />
                    <p>{t('orderAssignment.grandTotalRulesInfo')}</p>
                  </div>

                  {formData.grandTotalRules.length > 0 ? (
                    <div className={styles.rulesList}>
                      {formData.grandTotalRules.map((rule, index) => (
                        <div key={index} className={styles.ruleCard}>
                          <div className={styles.ruleHeader}>
                            <h4>{t('orderAssignment.ruleNumber', { number: index + 1 })}</h4>
                            <button
                              onClick={() => removeGrandTotalRule(index)}
                              className={styles.deleteButton}
                              title={t('orderAssignment.deleteRule')}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                          <div className={styles.ruleFields}>
                            <div className={styles.rangeInputs}>
                              <div className={styles.formGroup}>
                                <label>{t('orderAssignment.minimumAmount')}</label>
                                <input
                                  type="number"
                                  value={rule.min === null ? '' : rule.min}
                                  onChange={(e) =>
                                    updateGrandTotalRule(
                                      index,
                                      'min',
                                      e.target.value === '' ? null : parseFloat(e.target.value)
                                    )
                                  }
                                  placeholder={t('orderAssignment.noMinimum')}
                                  step="0.01"
                                  className={styles.input}
                                />
                              </div>
                              <div className={styles.rangeSeparator}>{t('orderAssignment.to')}</div>
                              <div className={styles.formGroup}>
                                <label>{t('orderAssignment.maximumAmount')}</label>
                                <input
                                  type="number"
                                  value={rule.max === null ? '' : rule.max}
                                  onChange={(e) =>
                                    updateGrandTotalRule(
                                      index,
                                      'max',
                                      e.target.value === '' ? null : parseFloat(e.target.value)
                                    )
                                  }
                                  placeholder={t('orderAssignment.noMaximum')}
                                  step="0.01"
                                  className={styles.input}
                                />
                              </div>
                            </div>
                            {errors[`grandTotal_${index}_range`] && (
                              <span className={styles.errorText}>{errors[`grandTotal_${index}_range`]}</span>
                            )}
                            <div className={styles.formGroup}>
                              <label>{t('orderAssignment.assignTo')} *</label>
                              <MultiSelectFilter
                                options={userOptions}
                                selectedValues={rule.userIds}
                                onChange={(userIds) => updateGrandTotalRule(index, 'userIds', userIds)}
                                placeholder={t('orderAssignment.selectUsers')}
                                searchPlaceholder={t('orderAssignment.searchUsers')}
                                displayKey="label"
                                valueKey="key"
                                error={errors[`grandTotal_${index}_users`]}
                              />
                            </div>
                            <div className={styles.rangePreview}>
                              {rule.min === null && rule.max === null
                                ? t('orderAssignment.rangePreviewZeroToInfinity')
                                : rule.max === null
                                ? t('orderAssignment.rangePreviewInfinity', { min: (rule.min || 0).toFixed(2) })
                                : t('orderAssignment.rangePreviewCurrency', { 
                                    min: (rule.min || 0).toFixed(2), 
                                    max: rule.max.toFixed(2) 
                                  })}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className={styles.emptyState}>
                      <DollarSign size={48} />
                      <p>{t('orderAssignment.noGrandTotalRules')}</p>
                      <button onClick={addGrandTotalRule} className={styles.emptyStateButton}>
                        <Plus size={16} />
                        {t('orderAssignment.addFirstRule')}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Product Rules */}
              <div className={styles.configSection}>
                <div className={styles.sectionHeader}>
                  <div className={styles.sectionTitleRow}>
                    <Package className={styles.sectionIcon} size={20} />
                    <h2>{t('orderAssignment.productRules')}</h2>
                  </div>
                  <button onClick={addProductRule} className={styles.addButton}>
                    <Plus size={16} />
                    {t('orderAssignment.addRule')}
                  </button>
                </div>
                <div className={styles.sectionContent}>
                  <div className={styles.infoBox}>
                    <Info size={16} />
                    <p>{t('orderAssignment.productRulesInfo')}</p>
                  </div>

                  {formData.productRules.length > 0 ? (
                    <div className={styles.rulesList}>
                      {formData.productRules.map((rule, index) => (
                        <div key={index} className={styles.ruleCard}>
                          <div className={styles.ruleHeader}>
                            <h4>{t('orderAssignment.ruleNumber', { number: index + 1 })}</h4>
                            <button
                              onClick={() => removeProductRule(index)}
                              className={styles.deleteButton}
                              title={t('orderAssignment.deleteRule')}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                          <div className={styles.ruleFields}>
                            <div className={styles.formGroup}>
                              <label>{t('orderAssignment.products')} *</label>
                              <MultiSelectFilter
                                options={productOptions}
                                selectedValues={rule.productIds}
                                onChange={(productIds) => updateProductRule(index, 'productIds', productIds)}
                                placeholder={t('orderAssignment.selectProducts')}
                                searchPlaceholder={t('orderAssignment.searchProducts')}
                                displayKey="label"
                                valueKey="key"
                                error={errors[`product_${index}_products`]}
                              />
                            </div>
                            <div className={styles.formGroup}>
                              <label>{t('orderAssignment.assignTo')} *</label>
                              <MultiSelectFilter
                                options={userOptions}
                                selectedValues={rule.userIds}
                                onChange={(userIds) => updateProductRule(index, 'userIds', userIds)}
                                placeholder={t('orderAssignment.selectUsers')}
                                searchPlaceholder={t('orderAssignment.searchUsers')}
                                displayKey="label"
                                valueKey="key"
                                error={errors[`product_${index}_users`]}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className={styles.emptyState}>
                      <Package size={48} />
                      <p>{t('orderAssignment.noProductRules')}</p>
                      <button onClick={addProductRule} className={styles.emptyStateButton}>
                        <Plus size={16} />
                        {t('orderAssignment.addFirstRule')}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Default Assignment */}
              <div className={styles.configSection}>
                <div className={styles.sectionHeader}>
                  <div className={styles.sectionTitleRow}>
                    <UsersIcon className={styles.sectionIcon} size={20} />
                    <h2>{t('orderAssignment.defaultAssignment')}</h2>
                  </div>
                </div>
                <div className={styles.sectionContent}>
                  <div className={styles.infoBox}>
                    <Info size={16} />
                    <p>{t('orderAssignment.defaultAssignmentInfo')}</p>
                  </div>

                  <div className={styles.formGroup}>
                    <label>{t('orderAssignment.defaultAssignees')}</label>
                    <MultiSelectFilter
                      options={userOptions}
                      selectedValues={formData.defaultUserIds}
                      onChange={updateDefaultUsers}
                      placeholder={t('orderAssignment.selectDefaultUsers')}
                      searchPlaceholder={t('orderAssignment.searchUsers')}
                      displayKey="label"
                      valueKey="key"
                    />
                  </div>
                </div>
              </div>

              {/* Last Updated Info */}
              {rulesData && (
                <div className={styles.metadataSection}>
                  <div className={styles.metadataItem}>
                    <Clock size={16} />
                    <span>{t('orderAssignment.lastUpdated', { date: formatDateTime(rulesData.updatedAt) })}</span>
                  </div>
                  <div className={styles.metadataItem}>
                    <User size={16} />
                    <span>
                      {rulesData.updatedBy?.name 
                        ? t('orderAssignment.updatedByUser', { name: rulesData.updatedBy.name })
                        : t('orderAssignment.updatedByUnknown')}
                    </span>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className={styles.actionButtons}>
                <button
                  onClick={handleCancel}
                  className={styles.cancelButton}
                  disabled={isSaving}
                >
                  <X size={18} />
                  {t('orderAssignment.cancel')}
                </button>
                <button
                  onClick={handleSave}
                  className={styles.saveButton}
                  disabled={isSaving || !hasChanges}
                >
                  {isSaving ? (
                    <>
                      <div className={styles.loadingSpinner}></div>
                      {t('orderAssignment.saving')}
                    </>
                  ) : (
                    <>
                      <Save size={18} />
                      {t('orderAssignment.saveRules')}
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssignmentRulesConfigPage;